// hooks/useDictation.js

import { useRef, useState } from 'react';
import SpeechRecognition from 'react-speech-recognition';

const MAX_RESETS = 3;

export const useDictation = ({
  questions,
  formValues,
  setFormValues,
  browserSupportsSpeechRecognition,
  resetTranscript,
}) => {
  const [dictatingIndex, setDictatingIndex] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState({});
  const [usedRecordingSlots, setUsedRecordingSlots] = useState(new Set());
  const [resetCount, setResetCount] = useState({});
  const [pausedQuestions, setPausedQuestions] = useState(new Set());
  const timerRef = useRef(null);
  const baseTextRef = useRef('');
  const currentIndexRef = useRef(null); // ✅ Mantener referencia del índice

  const getMaxRecordingTime = (questionIndex) => {
    const question = questions[questionIndex];
    return question?.settings?.responseTimeoutSeconds || 60;
  };

  const startDictation = async (idx, currentValue) => {
    if (!browserSupportsSpeechRecognition) {
      alert('❌ Tu navegador no soporta reconocimiento de voz');
      return;
    }

    const MAX_TIME = getMaxRecordingTime(idx);
    const currentResets = resetCount[idx] || 0;

    if (currentResets >= MAX_RESETS) {
      alert(
        `⚠️ Ya has usado los ${MAX_RESETS} intentos permitidos para este campo`
      );
      return;
    }

    // Si hay otra pregunta dictando, detenerla
    if (dictatingIndex !== null && dictatingIndex !== idx) {
      SpeechRecognition.stopListening();
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setPausedQuestions(prev => new Set(prev).add(dictatingIndex));
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());

      baseTextRef.current = currentValue ?? formValues[idx]?.value ?? '';
      resetTranscript();
      
      currentIndexRef.current = idx; // ✅ Guardar en ref
      setDictatingIndex(idx);
      setIsRecording(true);

      setPausedQuestions(prev => {
        const newSet = new Set(prev);
        newSet.delete(idx);
        return newSet;
      });

      // Inicializar el tiempo para esta pregunta
      setRecordingTime(prev => {
        if (prev[idx] !== undefined) {
          return prev;
        }
        return { ...prev, [idx]: 0 };
      });

      // Iniciar el temporizador
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          const currentTime = prev[idx] || 0;
          const newTime = currentTime + 1;

          if (newTime >= MAX_TIME) {
            stopDictation(true, idx); // ✅ Pasar índice explícitamente
            return { ...prev, [idx]: MAX_TIME };
          }

          return { ...prev, [idx]: newTime };
        });
      }, 1000);

      await SpeechRecognition.startListening({
        continuous: true,
        interimResults: true,
        language: 'es-ES',
      });
    } catch (error) {
      console.error('❌ Error completo:', error);

      if (error.name === 'NotAllowedError') {
        alert(
          '🚫 Permisos denegados.\n\n' +
            'Para usar el dictado por voz:\n' +
            '1. Haz clic en el candado 🔒 de la barra de direcciones\n' +
            '2. Permite el acceso al micrófono\n' +
            '3. Recarga la página'
        );
      } else if (error.name === 'NotFoundError') {
        alert('🎤 No se detectó ningún micrófono en tu dispositivo');
      } else if (error.name === 'NotReadableError') {
        alert('⚠️ El micrófono está siendo usado por otra aplicación');
      } else if (error.name === 'NotSupportedError') {
        alert('❌ Tu navegador no soporta esta funcionalidad');
      } else {
        alert(`Error desconocido: ${error.message}`);
      }

      currentIndexRef.current = null;
      setDictatingIndex(null);
      setIsRecording(false);
    }
  };

  const pauseDictation = () => {
    const currentIdx = dictatingIndex; 
    SpeechRecognition.stopListening();
    setIsRecording(false);

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (currentIdx !== null) {
      setPausedQuestions(prev => new Set(prev).add(currentIdx));
    }
  };

  const resumeDictation = async () => {
    const MAX_TIME = getMaxRecordingTime(dictatingIndex);
    setIsRecording(true);

    setPausedQuestions(prev => {
      const newSet = new Set(prev);
      newSet.delete(dictatingIndex);
      return newSet;
    });

    timerRef.current = setInterval(() => {
      setRecordingTime((prev) => {
        const currentTime = prev[dictatingIndex] || 0;
        const newTime = currentTime + 1;

        if (newTime >= MAX_TIME) {
          stopDictation(true, dictatingIndex); // ✅ Pasar índice explícitamente
          return { ...prev, [dictatingIndex]: MAX_TIME };
        }

        return { ...prev, [dictatingIndex]: newTime };
      });
    }, 1000);

    await SpeechRecognition.startListening({
      continuous: true,
      interimResults: true,
      language: 'es-ES',
    });
  };

  const stopDictation = (reachedLimit = false, forceIndex = null) => {
    // ✅ Usar índice forzado, ref o estado (en ese orden de prioridad)
    const currentIndex = forceIndex ?? currentIndexRef.current ?? dictatingIndex;
    
    // Detener timer primero
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    SpeechRecognition.stopListening();
    setIsRecording(false);
    resetTranscript();
    baseTextRef.current = '';

    if (reachedLimit && currentIndex !== null) {
      
      setUsedRecordingSlots((prev) => {
        const newSet = new Set(prev);
        newSet.add(currentIndex);
        return newSet;
      });
      // ✅ NO resetear tiempo cuando alcanza límite
    } else if (currentIndex !== null) {
      
      const updatedValues = [...formValues];
      updatedValues[currentIndex] = {
        ...updatedValues[currentIndex],
        value: '',
      };
      setFormValues(updatedValues);

      setResetCount((prev) => ({
        ...prev,
        [currentIndex]: (prev[currentIndex] || 0) + 1,
      }));

      // ✅ Solo resetear tiempo cuando se detiene manualmente
      setRecordingTime(prev => ({ ...prev, [currentIndex]: 0 }));
    }
    if (currentIndex !== null) {
      setPausedQuestions(prev => {
        const newSet = new Set(prev);
        newSet.delete(currentIndex);
        return newSet;
      });
    }
    currentIndexRef.current = null;
    setDictatingIndex(null);
  };

  return {
    dictatingIndex,
    isRecording,
    recordingTime,
    usedRecordingSlots,
    resetCount,
    baseTextRef,
    pausedQuestions,
    startDictation,
    pauseDictation,
    resumeDictation,
    stopDictation,
    getMaxRecordingTime,
  };
};